import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Layers, UserCircle, Calendar, Dumbbell, ChevronRight, BookOpen } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useLoadPreferences } from '../../hooks/useLoadPreferences';
import { Header, Card, Button, EmptyState } from '../../components/ui';
import { ActivityFeed } from '../../components/ActivityFeed';
import { CurrentTrainingCard } from '../../components/CurrentTrainingCard';
import { athletesApi } from '../../api/athletes';
import { groupsApi } from '../../api/groups';
import { coachesApi } from '../../api/coaches';
import { eventsApi } from '../../api/events';
import { assistantsApi } from '../../api/assistants';
import { calendarApi } from '../../api/calendar';
import { trainingSessionsApi } from '../../api/trainingSessions';
import { todayISO } from '../../utils/attendance';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import type { CalendarItem } from '../../types/calendar';

// -- Helpers ------------------------------------------------------------------

// Цвета для виджетов сетки (Orange Drive Palette)
const WIDGET_COLORS = {
  athletes: '#F57C00', // var(--avatar-1)
  groups: '#E65100',   // var(--avatar-2)
  coaches: '#FB8C00',  // var(--avatar-3)
  events: '#FF9800',   // var(--avatar-4)
} as const;

// -- Widget ------------------------------------------------------------------

interface StatWidgetProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  limit?: number; // -1 = unlimited
  color: string;
  onClick: () => void;
}

const StatWidget: React.FC<StatWidgetProps> = ({
  icon,
  label,
  count,
  limit,
  color,
  onClick,
}) => {
  const limitText =
    limit !== undefined && limit !== -1 ? `/${limit}` : '';

  return (
    <Card className="flex flex-col items-center justify-center gap-1 py-4" onClick={onClick}>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-1"
        style={{ backgroundColor: `${color}18` }}
      >
        {icon}
      </div>
      <span
        className="text-2xl font-bold"
        style={{ color: 'var(--text-primary)' }}
      >
        {count}
        {limitText && (
          <span className="text-sm font-normal" style={{ color: 'var(--text-hint)' }}>
            {limitText}
          </span>
        )}
      </span>
      <span
        className="text-[13px]"
        style={{ color: 'var(--text-secondary)' }}
      >
        {label}
      </span>
    </Card>
  );
};

// -- Upcoming Event card -----------------------------------------------------

// -- This Week Widget --------------------------------------------------------

const DAY_NAMES_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d);
  mon.setDate(diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function addDaysHelper(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface ThisWeekWidgetProps {
  items: CalendarItem[];
  onClick: () => void;
}

const ThisWeekWidget: React.FC<ThisWeekWidgetProps> = ({ items, onClick }) => {
  const today = new Date();
  const weekStart = getMonday(today);
  const days = Array.from({ length: 7 }, (_, i) => addDaysHelper(weekStart, i));

  const dateCounts = useMemo(() => {
    const map = new Map<string, { trainings: number; events: number }>();
    items.forEach((item) => {
      const entry = map.get(item.date) || { trainings: 0, events: 0 };
      // Отменённое занятие тренировкой дня не считаем
      if (item.type === 'training') {
        if (!item.cancelled) entry.trainings++;
      } else entry.events++;
      map.set(item.date, entry);
    });
    return map;
  }, [items]);

  return (
    <Card onClick={onClick}>
      <div className="flex items-center justify-between mb-3">
        <span
          className="text-[13px] font-semibold"
          style={{ color: 'var(--text-secondary)' }}
        >
          Эта неделя
        </span>
        <ChevronRight size={16} style={{ color: 'var(--text-hint)' }} />
      </div>
      <div className="flex justify-between">
        {days.map((day) => {
          const dateStr = fmtDate(day);
          const isToday = fmtDate(today) === dateStr;
          const counts = dateCounts.get(dateStr);

          return (
            <div key={dateStr} className="flex flex-col items-center gap-1">
              <span
                className="text-[11px]"
                style={{ color: 'var(--text-hint)' }}
              >
                {DAY_NAMES_SHORT[day.getDay()]}
              </span>
              <span
                className="text-[14px] font-semibold w-7 h-7 flex items-center justify-center rounded-full"
                style={{
                  color: isToday ? '#FFFFFF' : 'var(--text-primary)',
                  backgroundColor: isToday ? 'var(--accent)' : 'transparent',
                }}
              >
                {day.getDate()}
              </span>
              <div className="flex gap-0.5 h-1.5">
                {counts?.trainings ? (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: 'var(--accent)' }}
                  />
                ) : null}
                {counts?.events ? (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: '#1976D2' }}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};


// -- Loading skeleton --------------------------------------------------------

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-4 px-4 pt-6">
    {/* header */}
    <div className="h-8 rounded w-40" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    <div className="h-4 rounded w-24" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    {/* grid */}
    <div className="grid grid-cols-2 gap-3 mt-6">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-28 rounded-xl"
          style={{ backgroundColor: 'var(--bg-secondary)' }}
        />
      ))}
    </div>
  </div>
);

// -- Main component ----------------------------------------------------------

export function HomeScreen() {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const { switchTab, pushScreen, openDirectionSwitcher } = useTabNavigationStore();
  const { isLoading: isLoadingPreferences } = useLoadPreferences();

  const { data: organization } = useCurrentOrganization();
  const {
    maxAthletes,
    maxGroups,
    maxCoaches,
    isLoading: isLoadingLimits,
  } = useSubscriptionLimits();

  // -- Data queries ----------------------------------------------------------

  const { data: assistant, isLoading: isLoadingAssistant } = useQuery({
    queryKey: ['assistant', currentAssistantId],
    queryFn: () => assistantsApi.getById(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: athletes, isLoading: isLoadingAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: groups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: coaches, isLoading: isLoadingCoaches } = useQuery({
    queryKey: ['coaches', currentAssistantId],
    queryFn: () => coachesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  // Что идёт по расписанию прямо сейчас. Обновляем раз в минуту: тренер
  // открывает главную по ходу тренировки, и виджет не должен отставать.
  const { data: currentTrainings } = useQuery({
    queryKey: ['current-trainings', currentAssistantId],
    queryFn: () => trainingSessionsApi.current(currentAssistantId!),
    enabled: !!currentAssistantId,
    refetchInterval: 60_000,
  });

  const { data: upcomingEvents } = useQuery({
    queryKey: ['events-upcoming', currentAssistantId],
    queryFn: () => eventsApi.getUpcoming(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  // Calendar data for "This week" widget
  const weekRange = useMemo(() => {
    const today = new Date();
    const mon = getMonday(today);
    const sun = addDaysHelper(mon, 7);
    return { from: fmtDate(mon), to: fmtDate(sun) };
  }, []);

  const { data: weekCalendar } = useQuery({
    queryKey: ['calendar-week', currentAssistantId, weekRange.from, weekRange.to],
    queryFn: () =>
      calendarApi.getCalendar(currentAssistantId!, {
        from: weekRange.from,
        to: weekRange.to,
      }),
    enabled: !!currentAssistantId,
  });

  // -- Derived state ---------------------------------------------------------

  const totalAthletes = athletes?.length ?? 0;
  const totalGroups = groups?.length ?? 0;
  const totalCoaches = coaches?.length ?? 0;
  const totalEvents = upcomingEvents?.length ?? 0;

  const isLoading =
    isLoadingAssistant || isLoadingAthletes || isLoadingGroups || isLoadingCoaches || isLoadingLimits || isLoadingPreferences;

  // -- Loading (including preferences) ---------------------------------------

  if (isLoading) {
    return <Skeleton />;
  }

  // -- No assistant selected (onboarding) ------------------------------------
  // Only show after preferences loaded to avoid flashing this screen

  if (!currentAssistantId) {
    return (
      <EmptyState
        icon={Dumbbell}
        title="Создайте первое направление"
        description="Направление -- это вид спорта или активности, которым вы занимаетесь. Например, Футбол или Йога."
        primaryAction={{
          label: 'Создать направление',
          onClick: () => openDirectionSwitcher(),
        }}
      />
    );
  }

  // -- Subtitle (organization name if user has org) --------------------------

  const subtitle = organization?.name ?? undefined;

  // -- Render ----------------------------------------------------------------

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="px-4 pt-4">
        {/* Header */}
        <Header
          title={assistant?.name ?? 'Главная'}
          subtitle={subtitle}
        />

        {/* Тренировка, которая идёт прямо сейчас — самый короткий путь к перекличке */}
        {currentTrainings && currentTrainings.length > 0 && (
          <div className="mb-3">
            <CurrentTrainingCard
              slots={currentTrainings}
              onOpen={(slot) =>
                pushScreen('attendance', {
                  groupId: slot.group_id,
                  date: todayISO(),
                  startTime: slot.start_time,
                })
              }
            />
          </div>
        )}

        {/* Stat Widgets 2x2 */}
        <div className="grid grid-cols-2 gap-3">
          <StatWidget
            icon={<Users size={22} color={WIDGET_COLORS.athletes} />}
            label="Спортсмены"
            count={totalAthletes}
            limit={maxAthletes}
            color={WIDGET_COLORS.athletes}
            onClick={() => switchTab('athletes')}
          />
          <StatWidget
            icon={<Layers size={22} color={WIDGET_COLORS.groups} />}
            label="Группы"
            count={totalGroups}
            limit={maxGroups}
            color={WIDGET_COLORS.groups}
            onClick={() => switchTab('groups')}
          />
          <StatWidget
            icon={<UserCircle size={22} color={WIDGET_COLORS.coaches} />}
            label="Тренеры"
            count={totalCoaches}
            limit={maxCoaches}
            color={WIDGET_COLORS.coaches}
            onClick={() => {
              switchTab('groups');
              pushScreen('coaches');
            }}
          />
          <StatWidget
            icon={<Calendar size={22} color={WIDGET_COLORS.events} />}
            label="События"
            count={totalEvents}
            color={WIDGET_COLORS.events}
            onClick={() => pushScreen('events')}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            variant="secondary"
            onClick={() => pushScreen('materials', { assistantId: currentAssistantId })}
            className="flex-1 !h-10 !text-[15px]"
          >
            <BookOpen size={16} className="mr-1" />
            Материалы
          </Button>
        </div>

        {/* This Week Widget */}
        <div className="mt-6">
          <ThisWeekWidget
            items={weekCalendar?.items || []}
            onClick={() => pushScreen('calendar')}
          />
        </div>

        {/* Activity Feed */}
        <div className="mt-6">
          <h2
            className="text-[15px] font-semibold mb-3"
            style={{ color: 'var(--text-secondary)' }}
          >
            Активность
          </h2>
          <ActivityFeed />
        </div>

        {/* Empty hint when nothing yet */}
        {totalAthletes === 0 && totalGroups === 0 && totalCoaches === 0 && (
          <div className="mt-8 text-center">
            <p
              className="text-[15px]"
              style={{ color: 'var(--text-hint)' }}
            >
              Добавьте спортсменов и группы, чтобы начать работу
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
